import { client } from '@/api/client';

// Thin wrapper around the pcmilerRoute edge function -- the only place in
// the app that should ever build a PC*Miler request. The API key lives
// server-side in the edge function; nothing here ever sees it.
//
// stops: array of 2+ stops in visit order, each either
//   { lat, lng }                              -- a live GPS point
//   { street?, city, state, zip? }             -- a structured address
// Returns { miles, hours, stateMileage?, hazmatRoutingApplied, error? } --
// callers check `.error` rather than a thrown exception, since "address
// not recognized" is an expected, common outcome here, not a bug.
export async function calcRoute(stops, { hazmat = false, includeStates = false } = {}) {
  try {
    const data = await client.functions.invoke('pcmilerRoute', { stops, hazmat, includeStates });
    return data || { error: 'PC*Miler request failed' };
  } catch (err) {
    return { error: err?.message || 'PC*Miler request failed' };
  }
}

// Parses a loose "City, ST" (or "City, State, Zip") string, as stored on
// Load.origin/destination, into a stop the edge function understands.
export function parseCityState(str) {
  const parts = (str || '').split(',').map(s => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return { city: parts[0], state: parts[1].slice(0, 2).toUpperCase() };
  }
  return { city: (str || '').trim(), state: '' };
}

// Parses the full "Street, City, ST, Zip" string stored on
// Load.pickup_address/dropoff_address (built by TrimbleAddressAutocomplete)
// into a stop. Returns null if it doesn't have at least street+city+state.
export function parseFullAddress(str) {
  const parts = (str || '').split(',').map(s => s.trim()).filter(Boolean);
  if (parts.length < 3) return null;
  return { street: parts[0], city: parts[1], state: parts[2].slice(0, 2).toUpperCase(), zip: parts[3] || '' };
}

// Prefers the full street address over the coarse "City, ST" pair for
// mileage lookups. Two stops in the same city but different street
// addresses -- a common local/drayage move -- collapse to an identical
// "City, ST" pair, and PC*Miler correctly reports a 0-mile route between
// two identical points even though the real drive covered real miles.
// The street address disambiguates them.
export function bestStop(fullAddress, cityStateStr) {
  return parseFullAddress(fullAddress) || parseCityState(cityStateStr);
}
